// Presence design tokens (PRD FR-PRES-01/02, D-110).
//
// Every state has three signals, never one: a colour, a distinct shape, and a
// text label. Colour alone fails WCAG 1.4.1, and red beside green is the pair
// people with the commonest colour-vision deficiency confuse. Offline is grey;
// red is kept for Do Not Disturb.
//
// Each colour meets 3:1 against its background (WCAG 1.4.11): the light values
// against white, the dark values against Ant Design's dark surface. The test
// beside this file computes the ratios, so a palette change that breaks one
// fails the build. The app has no dark theme yet; the dark values are ready for
// when it does.

export type PresenceState = 'active' | 'away' | 'offline' | 'dnd' | 'on_leave';

export type PresenceShape = 'filled' | 'clock' | 'ring' | 'bar' | 'arrow';

export interface PresenceToken {
  light: string;
  dark: string;
  shape: PresenceShape;
  /** Key in the collaboration i18n namespace. */
  labelKey: string;
}

export const PRESENCE_TOKENS: Record<PresenceState, PresenceToken> = {
  active: { light: '#15803D', dark: '#4ADE80', shape: 'filled', labelKey: 'presence.active' },
  away: { light: '#B45309', dark: '#FBBF24', shape: 'clock', labelKey: 'presence.away' },
  offline: { light: '#6B7280', dark: '#9CA3AF', shape: 'ring', labelKey: 'presence.offline' },
  dnd: { light: '#DC2626', dark: '#F87171', shape: 'bar', labelKey: 'presence.dnd' },
  on_leave: { light: '#7C3AED', dark: '#A78BFA', shape: 'arrow', labelKey: 'presence.onLeave' },
};

/** The surfaces the tokens are checked against. */
export const SURFACE = { light: '#FFFFFF', dark: '#141414' } as const;

/** WCAG 2.x relative luminance of a #RRGGBB colour. */
export function luminance(hex: string): number {
  const channel = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** WCAG 2.x contrast ratio between two #RRGGBB colours. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
